import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import {
  FaArrowRight,
  FaPlay,
  FaCog,
  FaTrash,
  FaGamepad,
  FaLock,
} from "react-icons/fa";
import { getGame, deleteGame, createWebGame } from "../../api/game";
import { useSubscription } from "../../contexts/SubscriptionContext";
import { queueEvent } from "../../analytics";
import { EVENTS } from "../../analytics/events";
import { Button, Card, Modal, PageSpinner } from "../../ui";

// Mirrors the app's قائمة اللعبة: start the game on the web (createWebGame →
// existing /game-menu/:code gameplay), edit, or delete.
const GameMenu = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isFreeTier, limit, showUpgradeModal } = useSubscription();
  const [confirmDelete, setConfirmDelete] = useState(false);

  const { data: game, isLoading } = useQuery({
    queryKey: ["game", id],
    queryFn: () => getGame(id),
  });

  const start = useMutation({
    mutationFn: () => createWebGame(id),
    onSuccess: (res) => {
      const code = res?.codeInfo?.code || res?.code;
      if (!code) {
        toast.error("تعذّر بدء اللعبة");
        return;
      }
      // Same pair the app fires when a game is launched on the web.
      queueEvent(EVENTS.GAME_WEB_CREATED, { gameId: id });
      queueEvent(EVENTS.GAME_STARTED, { gameId: id });
      navigate(`/game-menu/${code}`);
    },
    onError: () => toast.error("تعذّر بدء اللعبة"),
  });

  const remove = useMutation({
    mutationFn: () => deleteGame(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["myGames"] });
      queueEvent(EVENTS.GAME_DELETED, { gameId: id });
      toast.success("تم حذف اللعبة");
      navigate("/app/games");
    },
    onError: () => toast.error("تعذّر حذف اللعبة"),
  });

  if (isLoading) {
    return <PageSpinner />;
  }
  if (!game) {
    return <p className="text-white/70 text-center py-20">اللعبة غير موجودة</p>;
  }

  const hasLockedModes = (game.modes || []).some((m) => m.locked);
  const customModesCount = (game.customModes || []).length;
  const blocked =
    isFreeTier &&
    (hasLockedModes || customModesCount > limit("MAX_CUSTOM_MODES"));

  const handleStart = () => {
    if (blocked) {
      queueEvent(EVENTS.CTA_PAYWALL_SHOWN, {
        source: "game_start_blocked",
        reason: hasLockedModes ? "locked_modes" : "custom_modes_limit",
      });
      showUpgradeModal({ messageKey: "GENERIC" });
      return;
    }
    start.mutate();
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="flex items-center gap-3 mb-6 md:mb-8">
        <button
          type="button"
          onClick={() => navigate("/app/games")}
          className="shrink-0 w-10 h-10 rounded-xl border border-white/10 bg-white/[0.05] text-white/70 hover:text-white hover:bg-white/10 flex items-center justify-center"
          aria-label="رجوع إلى ألعابي"
        >
          <FaArrowRight aria-hidden="true" />
        </button>
        <div className="min-w-0">
          <h1 className="text-2xl md:text-3xl font-extrabold">قائمة اللعبة</h1>
          <p className="text-white/55 text-sm line-clamp-1">{game.name}</p>
        </div>
      </div>

      {/* Start */}
      <Card tone="accent" className="mb-4">
        <div className="flex items-center gap-3 mb-4">
          <span className="w-10 h-10 rounded-xl bg-mainButton text-white flex items-center justify-center shrink-0">
            <FaGamepad aria-hidden="true" />
          </span>
          <h2 className="text-lg font-extrabold">ابدأ اللعب</h2>
        </div>

        {blocked && (
          <div className="rounded-xl border border-primary/40 bg-primary/10 p-3 mb-4 flex items-start gap-2.5 text-sm text-white">
            <FaLock className="mt-0.5 shrink-0 text-primary" aria-hidden="true" />
            <span className="leading-relaxed">
              تحتوي على فئات مميزة أو فئات مجتمع تتجاوز حدّك. اشترك أو عدّل
              اللعبة.
            </span>
          </div>
        )}

        <Button
          block
          size="lg"
          onClick={handleStart}
          loading={start.isPending}
          icon={<FaPlay className="text-base" aria-hidden="true" />}
        >
          ابدأ اللعبة
        </Button>
      </Card>

      {/* Options */}
      <Card>
        <div className="flex items-center gap-2 mb-4">
          <FaCog className="text-white/55" aria-hidden="true" />
          <h2 className="font-extrabold">خيارات اللعبة</h2>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => navigate(`/app/games/${id}/edit`)}
            className="rounded-xl border border-white/10 bg-white/[0.05] hover:bg-white/10 hover:border-white/20 py-4 font-semibold flex flex-col items-center gap-2 transition-colors"
          >
            <FaCog className="text-primary text-lg" aria-hidden="true" />
            إعدادات اللعبة
          </button>
          <button
            type="button"
            onClick={() => setConfirmDelete(true)}
            className="rounded-xl border border-danger/30 bg-danger/10 hover:bg-danger/15 text-danger py-4 font-semibold flex flex-col items-center gap-2 transition-colors"
          >
            <FaTrash className="text-lg" aria-hidden="true" />
            حذف اللعبة
          </button>
        </div>
      </Card>

      <Modal
        open={confirmDelete}
        onClose={() => !remove.isPending && setConfirmDelete(false)}
        title="حذف اللعبة"
        size="sm"
      >
        <p className="text-white/70 leading-relaxed mb-6">
          هل تريد حذف "{game.name}"؟ لا يمكن التراجع.
        </p>
        <div className="flex gap-3">
          <Button
            variant="danger"
            className="flex-1"
            onClick={() => remove.mutate()}
            loading={remove.isPending}
            icon={<FaTrash aria-hidden="true" />}
          >
            حذف
          </Button>
          <Button
            variant="secondary"
            className="flex-1"
            onClick={() => setConfirmDelete(false)}
            disabled={remove.isPending}
          >
            إلغاء
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default GameMenu;
